/**
 * UN CUADRO DE LA PANTALLA DEL CLUB.
 *
 * Javier: «En la vista del club quiero que separe las vistas en cuadros para
 * poder entenderse mejor [...] pero sí quiero poder ver las diferencias tanto
 * para el administrador del club como para el jugador».
 *
 * QUÉ PROBLEMA RESUELVE. La pantalla del club son once secciones seguidas, una
 * debajo de otra, todas con el mismo peso: solicitudes, torneos, interruptores,
 * ranking, categorías, jugadores, datos, reglamento, reportes. Quien la abre no
 * ve grupos, ve una lista larga — y las cosas que solo hace quien administra
 * están intercaladas con las que ve cualquiera, así que no hay forma de saber
 * de un vistazo qué es de cada quien.
 *
 * Este cuadro agrupa lo que va junto, le pone un título y —cuando es solo para
 * quien administra— lo dice con una etiqueta.
 *
 * LA ETIQUETA ES TEXTO, NO UN COLOR. «Solo quien administra» se lee; un borde
 * de otro color hay que aprenderlo, y quien tiene daltonismo no lo aprende
 * nunca. El color acompaña, no informa.
 *
 * NO SE DIBUJA VACÍO. Un cuadro con título y nada adentro es peor que no
 * tenerlo: ocupa lugar, promete algo y no lo cumple. Cuando todo lo de adentro
 * está apagado —porque quien mira no administra— el cuadro entero desaparece.
 */
import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

interface Props {
  title: string;
  /** Una línea de qué es esto. Opcional: hay cuadros que se explican solos. */
  help?: string;
  /**
   * SOLO PARA QUIEN ADMINISTRA, y se avisa con una etiqueta.
   *
   * No decide QUIÉN lo ve —eso lo decide quien lo dibuja, que ya sabe si puede
   * administrar— sino si lleva la marca. Un cuadro que solo le llega a quien
   * administra igual se beneficia de decirlo: es lo que le contesta «¿esto lo
   * ven los jugadores?», que es la pregunta de Javier.
   */
  adminOnly?: boolean;
  children: ReactNode;
}

export function ClubBlock({ title, help, adminOnly = false, children }: Props): JSX.Element {
  const { t } = useTranslation();

  return (
    <section className={adminOnly ? 'club-block club-block-admin' : 'club-block'}>
      <div className="club-block-head">
        <h2>{title}</h2>
        {adminOnly && <span className="tag tag-admin">{t('communities.onlyAdmins')}</span>}
      </div>
      {help !== undefined && <p className="help club-block-help">{help}</p>}
      {children}
    </section>
  );
}
